// OP-3605 — Regras puras da tela de contratos, espelho de `domain/vinculo.ts`
// (podeEncerrarVinculo, REMESSA_ABERTA) e de `validarData` para VIGENCIA.
// Gates visuais; quem recusa de verdade é o servidor.
import { EstoqueLocal, NaturezaVinculo, RemessaRow, StatusRemessa, StatusVinculo, VinculoRow } from '../../../../types/estoque'

export const STATUS_VINCULO_UI: Record<StatusVinculo, { bg: string; color: string; label: string }> = {
  VIGENTE: { bg: '#D1FAE5', color: '#065F46', label: 'Vigente' },
  // Encerrado não é fracasso nem pendência: é o fim normal.
  ENCERRADO: { bg: '#F3F4F6', color: '#374151', label: 'Encerrado' },
  CANCELADO: { bg: '#F3F4F6', color: '#374151', label: 'Cancelado' },
}

export const NATUREZA_LABEL: Record<NaturezaVinculo, string> = {
  CONTRATO: 'Contrato de comodato',
  TERMO: 'Termo de responsabilidade',
}

/**
 * Remessas que ainda respondem por equipamento fora da base — espelho de
 * `REMESSA_ABERTA` do domínio. `ENTREGUE` está aqui de propósito: é o período
 * inteiro em que o equipamento está com o produtor.
 */
export const REMESSA_ABERTA: StatusRemessa[] = ['EM_SEPARACAO', 'DESPACHADA', 'ENTREGUE', 'EM_RETORNO', 'EM_CONFERENCIA']

const MS_DIA = 86400000
const utc = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)))

/** Dias de calendário de `de` até `ate` (ISO); negativo quando `ate` já passou. */
export function diasEntre(de: string, ate: string): number {
  return Math.round((utc(ate) - utc(de)) / MS_DIA)
}

/** Vigência a partir deste prazo (em dias) já é aviso. */
export const AVISO_VENCIMENTO_DIAS = 15

export type Vigencia =
  | { tipo: 'A_DEFINIR' }
  | { tipo: 'VENCIDA'; dias: number }
  | { tipo: 'VENCE_EM_BREVE'; dias: number }
  | { tipo: 'OK'; dias: number }

/** A vigência do INSTRUMENTO — prazo documental, não do equipamento. */
export function vigenciaDe(vigenciaFim: string | null, hoje: string): Vigencia {
  if (!vigenciaFim) return { tipo: 'A_DEFINIR' }
  const dias = diasEntre(hoje, vigenciaFim)
  if (dias < 0) return { tipo: 'VENCIDA', dias: -dias }
  if (dias <= AVISO_VENCIMENTO_DIAS) return { tipo: 'VENCE_EM_BREVE', dias }
  return { tipo: 'OK', dias }
}

export function statusDoVinculoUi(v: Pick<VinculoRow, 'status' | 'vigenciaFim'>, hoje: string): { bg: string; color: string; label: string } {
  const base = STATUS_VINCULO_UI[v.status] ?? { bg: '#F3F4F6', color: '#374151', label: v.status }
  if (v.status === 'VIGENTE' && vigenciaDe(v.vigenciaFim, hoje).tipo === 'VENCIDA') {
    return { bg: '#FEF3C7', color: '#92400E', label: 'Vigente · vencido' }
  }
  return base
}

export function avisoDoVinculoNaExpedicao(vigenciaFim: string | null, hoje: string, retornoPrevisto?: string | null): string | null {
  if (vigenciaDe(vigenciaFim, hoje).tipo !== 'VENCIDA') return null
  return retornoPrevisto
    ? 'vencido: a expedição estende até o retorno previsto'
    : 'vencido e sem retorno previsto: defina a vigência do contrato antes de expedir'
}

/** A linha que dói: instrumento vencido e o equipamento continua fora. */
export function vencidoComPeca(v: Pick<VinculoRow, 'status' | 'vigenciaFim' | 'pecasFora'>, hoje: string): boolean {
  return v.status === 'VIGENTE' && Number(v.pecasFora) > 0 && vigenciaDe(v.vigenciaFim, hoje).tipo === 'VENCIDA'
}

export interface Pendencia { texto: string }

/**
 * Pendências de patrimônio do vínculo: peça extraviada ainda no patrimônio e
 * proposta de baixa aberta. Não entram em `vencidoComPeca`/`atrasoRetorno`
 * (o equipamento não vai voltar), mas impedem tratar o vencido como só cadastro.
 */
export function pendenciasDePatrimonio(v: Pick<VinculoRow, 'pecasExtraviadas' | 'baixasPendentes'>): Pendencia[] {
  const out: Pendencia[] = []
  const extraviadas = Number(v.pecasExtraviadas) || 0
  const baixas = Number(v.baixasPendentes) || 0
  if (extraviadas > 0) out.push({ texto: `${extraviadas} extraviada${extraviadas === 1 ? '' : 's'}` })
  if (baixas > 0) out.push({ texto: `${baixas} baixa${baixas === 1 ? '' : 's'} pendente${baixas === 1 ? '' : 's'}` })
  return out
}

/**
 * O atraso de RETORNO é outra pergunta que a de vigência: o equipamento
 * passou do previsto. Dias de atraso, ou 0 quando não há o que cobrar.
 */
export function atrasoRetorno(proximoRetorno: string | null, pecasFora: number, hoje: string): number {
  if (!proximoRetorno || Number(pecasFora) <= 0) return 0
  return Math.max(0, diasEntre(proximoRetorno, hoje))
}

/** Espelho de `podeEncerrarVinculo`: o impedimento escrito, ou null. */
export function impedimentoEncerrar(status: StatusVinculo, remessasAbertas: number): string | null {
  if (status === 'ENCERRADO') return 'Este contrato já foi encerrado.'
  if (status === 'CANCELADO') return 'Este contrato foi cancelado.'
  const n = Number(remessasAbertas)
  if (n > 0) return `${n} remessa${n === 1 ? '' : 's'} em aberto — encerrar não devolve equipamento.`
  return null
}

/**
 * Espelho de `validarData` (campo VIGENCIA, obrigatória, ISO). O servidor não
 * impõe mínimo: data no passado é aceita e deixa o contrato vencido — a tela
 * avisa (`avisoVigencia`) em vez de impedir.
 */
export function impedimentoVigencia(data: string): string | null {
  const d = data.trim()
  if (!d) return 'Informe a data de vigência.'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return 'Data de vigência inválida.'
  return null
}

export function avisoVigencia(data: string, hoje: string): string | null {
  const d = data.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || d >= hoje) return null
  return 'A data está no passado: o contrato ficará vencido.'
}

/** Limite de retorno de uma remessa: prorrogação manda sobre a previsão. */
export const limiteRetorno = (r: Pick<RemessaRow, 'prorrogadoPara' | 'previsaoRetorno'>): string | null =>
  r.prorrogadoPara ?? r.previsaoRetorno

/**
 * Quem vai vencer primeiro no topo, dentro da página que veio.
 *
 * O CORTE entre abertas e fechadas é do servidor (`abertas=1|0`): separá-las
 * aqui só funcionaria se a tela tivesse todas as remessas do contrato, e ela
 * tem 25 por página — 25 encerradas recentes escondiam a antiga ainda aberta e
 * a tela declarava "nada fora por este contrato".
 */
export function ordenarPorRetorno(rows: RemessaRow[]): RemessaRow[] {
  return rows.slice().sort((a, b) =>
    String(limiteRetorno(a) ?? '9').localeCompare(String(limiteRetorno(b) ?? '9')))
}

/** A remessa ainda responde por equipamento fora? (espelho de `remessaEstaAberta`) */
export const remessaEstaAberta = (status: string): boolean =>
  REMESSA_ABERTA.indexOf(status as StatusRemessa) >= 0


/**
 * O nome da praça de um vínculo.
 *
 * `getEstoqueLocais` devolve DOIS números por linha — `id` do est_local e
 * `pracaId` — e eles são diferentes. O vínculo carrega a PRAÇA do detentor,
 * então a busca é por `pracaId`; casar com `id` acerta só por coincidência
 * numérica e, quando não coincide, cai no rótulo genérico ou mostra o nome de
 * outra praça.
 */
export function nomeDaPraca(locais: EstoqueLocal[], pracaId: number | null): string {
  if (pracaId == null) return 'sem praça'
  const l = locais.find((x) => x.pracaId === pracaId)
  return l ? l.nome : `praça ${pracaId}`
}
